import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useIsSpeaking } from '@livekit/components-react';
import { useTranslate } from '@tolgee/react';
import { MiniWave } from '../GenericComponents/AudioWaveformBadge';
import { colors } from '../../Assets/styles/colors';
import { RaisedHandIcon } from './RaisedHands';

const COMPONENT_WIDTH = 350;
const COMPONENT_HEIGHT = 200;
const PADDING = 16;

const SmallOwnCameraComponent = ({ participant, containerRef }) => {
    const { t } = useTranslate();
    const videoRef = useRef(null);
    const isUserspeaking = useIsSpeaking(participant);
    const [position, setPosition] = useState({ x: 55, y: 55 });
    const [isDragging, setIsDragging] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });

    const clamp = (val, min, max) => Math.max(min, Math.min(val, max));

    useEffect(() => {
        const videoTrack = participant.getTrackPublications().find(
            (track) => track.kind === "video" && track.track != null
        )?.track;

        if (videoTrack && videoRef.current) {
            videoTrack.attach(videoRef.current);
            return () => {
                videoTrack.detach(videoRef.current);
            };
        }
    }, [participant]);

    useEffect(() => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setPosition({
            x: rect.width - COMPONENT_WIDTH - PADDING,
            y: rect.height - COMPONENT_HEIGHT - PADDING,
        });
    }, []);

    const handleResize = () => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setPosition(prev => ({
            x: clamp(prev.x, PADDING, rect.width - COMPONENT_WIDTH - PADDING),
            y: clamp(prev.y, PADDING, rect.height - COMPONENT_HEIGHT - PADDING),
        }));
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [position]);

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(handleResize);
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [containerRef.current]);

    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
        setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            setPosition({
                x: clamp(e.clientX - startPos.x, PADDING, rect.width - COMPONENT_WIDTH - PADDING),
                y: clamp(e.clientY - startPos.y, PADDING, rect.height - COMPONENT_HEIGHT - PADDING),
            });
        };

        const handleMouseUp = () => setIsDragging(false);

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, startPos]);

    return (
        <ConnectingContainer
            onMouseDown={handleMouseDown}
            $isSpeaking={isUserspeaking}
            style={{ left: position.x, top: position.y }}
        >
            <RaisedHandIcon identity={participant.identity} />
            <VideoWrapper>
                <video ref={videoRef} autoPlay muted playsInline />
            </VideoWrapper>

            <SpeakingBadge>
                {isUserspeaking && <MiniWave small />}
                <BadgeInfo>
                    <BadgeName $speaking={isUserspeaking}>
                        {participant.name}
                    </BadgeName>
                    {isUserspeaking && (
                        <BadgeStatusRow>
                            <BadgeText>{t('call_modal.speaking') + '...'}</BadgeText>
                        </BadgeStatusRow>
                    )}
                </BadgeInfo>
            </SpeakingBadge>
        </ConnectingContainer>
    );
};

export default SmallOwnCameraComponent;

const ConnectingContainer = styled.div`
    width: ${COMPONENT_WIDTH}px;
    height: ${COMPONENT_HEIGHT}px;
    min-width: ${COMPONENT_WIDTH}px;
    min-height: ${COMPONENT_HEIGHT}px;
    position: absolute;
    cursor: move;
    z-index: 100;
    border-radius: 12px;
    overflow: hidden;
    user-select: none;
    flex-shrink: 0;
    box-shadow: -1px 4px 4px 0px rgba(0, 0, 0, 0.25);
    border: 2px solid ${({ $isSpeaking }) => $isSpeaking ? colors.speakingGreen : 'transparent'};
    transition: border-color 0.3s ease;
`;

const SpeakingBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    position: absolute;
    bottom: 10px;
    left: 10px;
    padding: 5px 10px;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.82), rgba(0, 0, 0, 0.68));
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
`;

const BadgeInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
`;

const BadgeName = styled.p`
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.2px;
    color: ${({ $speaking, $emotii }) =>
        $emotii ? colors.emotiiPurple
        : $speaking ? colors.speakingGreen
        : 'rgba(255, 255, 255, 0.92)'};
    transition: color 0.3s ease;
`;

const BadgeStatusRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const BadgeText = styled.span`
    font-size: 9px;
    font-weight: 400;
    color: ${colors.speakingGreen};
    opacity: 0.9;
`;

const VideoWrapper = styled.div`
    width: 100%;
    height: 100%;
    background: #111;

    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
`;

